import { Injectable, OnDestroy } from '@angular/core';
import { function as F } from 'fp-ts';
import * as mobx from 'mobx';
import { MonoTypeOperatorFunction, Observable, Subject } from 'rxjs';
import * as rx from 'rxjs/operators';
import { toIO, toRXWithOptions } from '../utils/mobx';

@Injectable()
export class BaseStore implements OnDestroy {
  protected readonly destroy$ = new Subject<void>();
  protected readonly disposers: mobx.IReactionDisposer[] = [];

  /**
   * Creates rxjs's take until function and will trigger destroy on ngOnDestroy.
   */
  readonly takeUntilDestroy: <T>() => MonoTypeOperatorFunction<T> = () =>
    rx.takeUntil(this.destroy$);

  /**
   * Converts mobx observable field to rxjs's Observable.
   */
  readonly toRXWithOptions = toRXWithOptions(this);

  /**
   * Converts mobx observable field to rxjs's Observable.
   */
  readonly toRX = <K extends keyof this>(key: K): Observable<this[K]> =>
    F.pipe(key, this.toRXWithOptions());

  /**
   * Mobx reaction wrapper that will run disposer on ngOnDestroy.
   */
  readonly reaction = <T>(
    expression: (r: mobx.IReactionPublic) => T,
    effect: (arg: T, prev: T | undefined, r: mobx.IReactionPublic) => void,
    opts?: mobx.IReactionOptions<T, boolean>,
  ): mobx.IReactionDisposer => {
    const disposer = mobx.reaction(expression, effect, opts);

    this.disposers.push(disposer);

    return disposer;
  };

  /**
   * Mobx autorun wrapper that will run disposer on ngOnDestroy.
   */
  readonly autorun: typeof mobx.autorun = (
    view: (r: mobx.IReactionPublic) => any,
    opts?: mobx.IAutorunOptions,
  ): mobx.IReactionDisposer => {
    const disposer = mobx.autorun(view, opts);

    this.disposers.push(disposer);

    return disposer;
  };

  readonly toIO = toIO(this);

  ngOnDestroy(): void {
    this.disposers.forEach(x => typeof x === 'function' && x());
    this.destroy$.next();
    this.destroy$.complete();
  }
}
